import React from "react"
import { graphql, useFragment } from "react-relay"

import { ViewsProvider_gql$key } from "~/Views/__generated__/ViewsProvider_gql.graphql"

import { getView } from "~/Views/helpers"

type Props = {
  gql: ViewsProvider_gql$key
  children: React.ReactNode
}
export type VirtualViewAlias = "ALL" | "MY_VIEW"
export type ViewID = number | VirtualViewAlias

type BaseView = {
  projectFilters: any
  peopleFilters: any
  name: string
  description: string
}

export type VirtualView = BaseView & {
  alias: VirtualViewAlias
}

export type DatabaseView = BaseView & {
  id: string
  databaseId: number
  userIds: ReadonlyArray<number>
}

export type AvailableView = VirtualView | DatabaseView
export type AvailableViewID =
  | { alias?: VirtualView["alias"] }
  | { databaseId?: DatabaseView["databaseId"] }

type ViewsContextType = {
  enabled: boolean
  selectedView: (DatabaseView | VirtualView | null) & {
    isCoerced: boolean
  }
  databaseViews: ReadonlyArray<DatabaseView>
  virtualViews: ReadonlyArray<VirtualView>
  availableViews: ReadonlyArray<DatabaseView | VirtualView>
}

const FRAGMENT = graphql`
  fragment ViewsProvider_gql on query_root {
    allViews: availableViews {
      ... on AvailableViewInterface {
        name
        description
        type
        projectFilters
        peopleFilters
      }
      ... on DatabaseView {
        id
        databaseId
        userIds
      }
      ... on VirtualView {
        id
        alias
      }
    }
    selectedView {
      ... on SelectedDatabaseView {
        type
        databaseId
        isCoerced
      }
      ... on SelectedVirtualView {
        alias
        isCoerced
      }
    }
    features_accounts {
      feature_id
      enabled
    }
  }
`

const ViewsContext = React.createContext<ViewsContextType>({
  databaseViews: [],
  virtualViews: [],
  availableViews: [],
  selectedView: null,
  enabled: false,
})

const ViewsProvider = (props: Props) => {
  const { children, gql } = props

  const data = useFragment<ViewsProvider_gql$key>(FRAGMENT, gql)
  const { allViews } = data
  // Temporary, until we remote the feature flag
  // ServerFlagsProvider isn't loaded yet in the planner
  const viewsEnabled =
    data.features_accounts.find(
      (feat) => feat.feature_id === "pre_filtered_views",
    )?.enabled ?? false

  const databaseViews = allViews
    .filter((v) => v.type === "database")
    .map((v) => ({
      id: v.id ?? "",
      databaseId: v.databaseId ?? 0,
      projectFilters: v.projectFilters ?? null,
      peopleFilters: v.peopleFilters ?? null,
      name: v.name ?? "",
      description: v.description ?? "",
      userIds: v.userIds ?? [],
    }))

  const virtualViews = allViews
    .filter((v) => v.type === "virtual")
    .map((v) => ({
      id: v.id ?? "",
      alias: v.alias as VirtualViewAlias,
      name: v.name ?? "",
      description: v.description ?? "",
      projectFilters: v.projectFilters ?? null,
      peopleFilters: v.peopleFilters ?? null,
    }))
  const availableViews = [...databaseViews, ...virtualViews]
  const selectedAvailableView =
    data.selectedView && getView(availableViews, data.selectedView)
  const selectedView = selectedAvailableView
    ? {
        ...selectedAvailableView,
        isCoerced: data.selectedView?.isCoerced,
      }
    : null

  const value = {
    databaseViews,
    virtualViews,
    availableViews,
    enabled: viewsEnabled,
    selectedView,
  }

  return <ViewsContext.Provider value={value}>{children}</ViewsContext.Provider>
}

export { ViewsContext, ViewsProvider }
